import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { revertApprovedCharges } from "../../api/finance/addChargesApi";
import AlertConfirmation from "../../components/input/AlertConfirmation";

const APPROVED_STATUS = 3;

const REVERT_CONFIG = {
    1: {
        name: "Invoice",
        statusField: "invStatus",
    },
    2: {
        name: "EV",
        statusField: "evStatus",
    },
};

const ChargeRevertConfirmation = ({
                                      open,
                                      type,
                                      selectedRows = [],
                                      onClose,
                                      onCompleted,
                                  }) => {
    const [isLoading, setIsLoading] = useState(false);

    const config = REVERT_CONFIG[type];

    const approvedRows = useMemo(() => {
        if (!config) {
            return [];
        }

        return selectedRows.filter(
            (row) =>
                row.jobId != null &&
                row.chgId != null &&
                Number(row[config.statusField]) === APPROVED_STATUS,
        );
    }, [selectedRows, config]);

    const ignoredCount = selectedRows.length - approvedRows.length;

    const message = useMemo(() => {
        if (!config) {
            return "";
        }

        let confirmationMessage =
            `Revert ${config.name} status for ` +
            `${approvedRows.length} approved ` +
            `charge${approvedRows.length === 1 ? "" : "s"}?`;

        if (ignoredCount > 0) {
            confirmationMessage +=
                ` ${ignoredCount} non-approved or empty ` +
                `row${ignoredCount === 1 ? "" : "s"} will be ignored.`;
        }

        return confirmationMessage;
    }, [config, approvedRows.length, ignoredCount]);

    const handleConfirm = async () => {
        if (isLoading) {
            return;
        }

        if (!config) {
            toast.error("Invalid charge revert type.", {
                autoClose: 5000,
                containerId: "Main",
            });

            onClose?.();
            return;
        }

        if (approvedRows.length === 0) {
            toast.warning(
                `None of the selected charges have Approved ${config.name} status.`,
                {
                    autoClose: 5000,
                    containerId: "Main",
                },
            );

            onClose?.();
            return;
        }

        try {
            setIsLoading(true);

            const response = await revertApprovedCharges({
                type,
                charges: approvedRows.map((row) => ({
                    jobId: row.jobId,
                    chgId: row.chgId,
                })),
            });

            const reverted = response?.reverted ?? 0;
            const skipped = response?.skipped ?? 0;
            const failed = response?.failed ?? 0;
            const totalIgnored = ignoredCount + skipped;

            const resultMessage =
                `${config.name} revert completed: ` +
                `${reverted} reverted, ` +
                `${totalIgnored} ignored/skipped, ` +
                `${failed} failed.`;

            if (failed > 0 && reverted === 0) {
                toast.error(resultMessage, {
                    autoClose: 7000,
                    containerId: "Main",
                });
            } else if (failed > 0 || totalIgnored > 0) {
                toast.warning(resultMessage, {
                    autoClose: 7000,
                    containerId: "Main",
                });
            } else {
                toast.success(resultMessage, {
                    autoClose: 5000,
                    containerId: "Main",
                });
            }

            onCompleted?.(response);
            onClose?.();
        } catch (error) {
            console.error("Charge revert failed", error);

            toast.error(
                error?.response?.data?.message ||
                "Charge status revert failed.",
                {
                    autoClose: 5000,
                    containerId: "Main",
                },
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            onClose?.();
        }
    };

    return (
        <AlertConfirmation
            open={open}
            message={message}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            confirmButton="Revert"
            cancelButton="Cancel"
            isLoading={isLoading}
            loadingMessage={`Reverting ${config?.name ?? ""} status...`}
        />
    );
};

export default ChargeRevertConfirmation;